import type { TokenView } from '@/lib/auth/types'

import { getDeviceId } from './deviceId'
import { STORAGE_KEYS } from './storageKeys'

/**
 * localStorage 래퍼 — **토큰의 진실의 원천**(설계 불변식 1).
 *
 * Zustand 는 표시용 미러일 뿐이며 여기 저장된 값이 정본이다.
 * 인터셉터(React 밖)와 라우트 가드(React 안)가 같은 소스를 읽어야 어긋나지 않는다.
 */

function readNumber(key: string): number {
  const raw = localStorage.getItem(key)
  if (raw === null) return 0
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * **epoch millis 문자열 → number** (설계 T2).
 *
 * 🚨 facecock-was 는 `X-Access-Token-Expires-At` 을 `Instant.toEpochMilli().toString()` 으로
 * 내려준다(`JwtToken.kt` 실측). ISO-8601 이 아니다. 선례 저장소(competition-admin)의
 * `Date.parse(iso)` 를 그대로 옮기면 `Date.parse("1754…")` → NaN 이 되어 **만료 판정이
 * 영구 무력화**된다. 그래서 파서를 분리하고 회귀 테스트로 고정한다.
 *
 * 정수 문자열만 받는다 — ISO 문자열이 들어오면 `null` 을 돌려 fail-closed 로 떨어뜨린다.
 */
export function parseEpochMillis(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? raw : null
  if (typeof raw !== 'string') return null

  const trimmed = raw.trim()
  if (!/^\d+$/.test(trimmed)) return null

  const parsed = Number(trimmed)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export const tokenStore = {
  get accessToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.accessToken)
  },

  get refreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.refreshToken)
  },

  get accessTokenExpiresAt(): number {
    return readNumber(STORAGE_KEYS.accessTokenExpiresAt)
  },

  get refreshTokenExpiresAt(): number {
    return readNumber(STORAGE_KEYS.refreshTokenExpiresAt)
  },

  get deviceId(): string {
    return getDeviceId()
  },

  /** refresh 토큰이 살아 있으면 세션이 유효하다 — 라우트 가드의 판정 기준. */
  hasValidSession(): boolean {
    return Boolean(this.refreshToken) && this.refreshTokenExpiresAt > Date.now()
  },

  /**
   * 토큰 4종을 **원자적으로** 저장한다.
   *
   * 파싱 실패 정책 — fail-closed:
   *  - `accessTokenExpiresAt` 불량 → `0` 으로 저장해 다음 요청이 즉시 갱신을 돌게 한다.
   *  - `refreshTokenExpiresAt` 불량·토큰 문자열 누락 → **아무것도 쓰지 않고 `clear()`**.
   *    세션 수명의 상한을 모르는 상태를 유지하지 않는다.
   *
   * @returns 저장 성공 여부. false 면 호출부가 로그인 실패로 처리한다.
   */
  saveTokens(view: TokenView | null | undefined): boolean {
    const accessExp = parseEpochMillis(view?.accessTokenExpiresAt)
    const refreshExp = parseEpochMillis(view?.refreshTokenExpiresAt)
    const hasTokens = Boolean(view?.accessToken) && Boolean(view?.refreshToken)

    if (!view || !hasTokens || refreshExp === null) {
      this.clear()
      return false
    }

    if (accessExp === null) {
      console.warn('[tokenStore] accessTokenExpiresAt 파싱 실패 — 0 으로 저장해 즉시 갱신을 유도합니다.')
    }

    // 🚨 `setItem` 은 던질 수 있다(QuotaExceeded, Safari 프라이빗 모드 등).
    // 중간에 터지면 **반쪽 세션**이 남아 만료 판정이 어긋나므로 실패 시 전부 되돌린다.
    try {
      localStorage.setItem(STORAGE_KEYS.accessToken, view.accessToken)
      localStorage.setItem(STORAGE_KEYS.refreshToken, view.refreshToken)
      localStorage.setItem(STORAGE_KEYS.accessTokenExpiresAt, String(accessExp ?? 0))
      localStorage.setItem(STORAGE_KEYS.refreshTokenExpiresAt, String(refreshExp))
      return true
    } catch {
      console.warn('[tokenStore] 토큰 저장에 실패했습니다 — 세션을 만들지 않습니다.')
      this.clear()
      return false
    }
  },

  /**
   * ⚠️ `deviceId` · `lastVenueId` 는 남긴다.
   * - deviceId: 같은 브라우저 재로그인 시 서버 refresh 행을 재사용한다.
   * - lastVenueId: 재로그인 후 마지막 구장으로 되돌린다.
   */
  clear(): void {
    localStorage.removeItem(STORAGE_KEYS.accessToken)
    localStorage.removeItem(STORAGE_KEYS.refreshToken)
    localStorage.removeItem(STORAGE_KEYS.accessTokenExpiresAt)
    localStorage.removeItem(STORAGE_KEYS.refreshTokenExpiresAt)
    localStorage.removeItem(STORAGE_KEYS.loginId)
  },
}
